import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download, Eye, EyeOff } from 'lucide-react';
import { ChartRenderer } from '@/components/artifacts/ChartRenderer';
import { TableRenderer } from '@/components/artifacts/TableRenderer';
import { ImageRenderer } from '@/components/artifacts/ImageRenderer';
import { JsonRenderer } from '@/components/artifacts/JsonRenderer';
import { MarkdownRenderer } from '@/components/artifacts/MarkdownRenderer';
import { CodeRenderer } from '@/components/artifacts/CodeRenderer';
import { TextRenderer } from '@/components/artifacts/TextRenderer';
import type { Artifact } from '@/types';

interface ArtifactRendererProps {
  artifact: Artifact;
  className?: string;
}

export function ArtifactRenderer({ artifact, className }: ArtifactRendererProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const getArtifactType = (artifact: Artifact): string => {
    const { mimeType, name } = artifact;

    console.log('🔍 ArtifactRenderer - Determining type for:', {
      mimeType,
      name,
      dataType: typeof artifact.data,
      dataPreview: typeof artifact.data === 'string' ? artifact.data.substring(0, 100) : artifact.data
    });

    // Chart data (JSON with chart metadata)
    if (mimeType === 'application/json' && name.includes('chart')) {
      console.log('📊 Using ChartRenderer');
      return 'chart';
    }

    // Table data (CSV or JSON with tabular structure)
    if (mimeType === 'text/csv' || (mimeType === 'application/json' && name.includes('table'))) {
      console.log('📋 Using TableRenderer');
      return 'table';
    }

    // Images
    if (mimeType.startsWith('image/')) {
      console.log('🖼️ Using ImageRenderer');
      return 'image';
    }

    // Markdown
    if (mimeType === 'text/markdown' || name.endsWith('.md')) {
      console.log('📝 Using MarkdownRenderer');
      return 'markdown';
    }

    // Code files
    if (mimeType === 'text/plain' && (name.endsWith('.py') || name.endsWith('.js') || name.endsWith('.sql'))) {
      console.log('💻 Using CodeRenderer');
      return 'code';
    }

    // JSON data
    if (mimeType === 'application/json') {
      console.log('📄 Using JsonRenderer');
      return 'json';
    }

    // Default to raw text
    console.log('📝 Using TextRenderer (default)');
    return 'text';
  };

  const renderArtifactContent = () => {
    const type = getArtifactType(artifact);

    switch (type) {
      case 'chart':
        return <ChartRenderer artifact={artifact} />;
      case 'table':
        return <TableRenderer artifact={artifact} />;
      case 'image':
        return <ImageRenderer artifact={artifact} />;
      case 'json':
        return <JsonRenderer artifact={artifact} />;
      case 'markdown':
        return <MarkdownRenderer artifact={artifact} />;
      case 'code':
        return <CodeRenderer artifact={artifact} />;
      default:
        return <TextRenderer artifact={artifact} />;
    }
  };

  const handleDownload = () => {
    const blob = new Blob([
      typeof artifact.data === 'string' ? artifact.data : JSON.stringify(artifact.data, null, 2)
    ], { type: artifact.mimeType });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = artifact.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const artifactType = getArtifactType(artifact);

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        {/* DEBUG: Show what renderer is being used */}
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-3 py-2 rounded text-xs mb-3">
          🔧 DEBUG: Using {artifactType} renderer | MimeType: {artifact.mimeType} | Name: {artifact.name}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">{artifact.name}</CardTitle>
            <Badge variant="secondary" className="text-xs">
              {artifactType}
            </Badge>
            <Badge variant="outline" className="text-xs">
              v{artifact.version}
            </Badge>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-8 w-8 p-0"
            >
              {isExpanded ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="h-8 w-8 p-0"
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {artifact.metadata && (
          <div className="text-xs text-muted-foreground">
            {Object.entries(artifact.metadata).map(([key, value]) => (
              <span key={key} className="mr-3">
                {key}: {String(value)}
              </span>
            ))}
          </div>
        )}
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          {renderArtifactContent()}
        </CardContent>
      )}
    </Card>
  );
}